/**
 * IRChat Secret Chat Protocol v1
 * ──────────────────────────────
 *
 * A pragmatic, well-defined end-to-end encrypted 1:1 protocol built on
 * WebCrypto primitives only.
 *
 * # Roles
 *
 *   - "Initiator" (Alice): the side that creates the chat and sends the first
 *     message. Performs X3DH-lite locally, derives the master secret, and
 *     ships an `init` envelope.
 *   - "Responder" (Bob): receives the `init` envelope, performs the matching
 *     half of X3DH-lite, derives the same master secret, and replies.
 *
 * # Long-term keys (per user, published once via `SecretChatPrekeyBundle`)
 *
 *   - IK_sign : ECDSA P-256, used to sign the rest of the bundle.
 *   - IK_dh   : ECDH P-256, used to authenticate the user during X3DH.
 *   - SPK     : ECDH P-256 signed prekey, rotated periodically.
 *   - {OPK_i} : ECDH P-256 one-time prekeys, each consumed at most once by
 *               an initiator.
 *
 * Bob signs `IK_dh` and `SPK` with `IK_sign`. Alice MUST verify both
 * signatures before deriving any secret.
 *
 * # Session establishment (X3DH-lite)
 *
 * When Alice starts a chat with Bob she:
 *
 *   1. Fetches Bob's bundle: { IK_sign_pub, IK_dh_pub, IK_dh_sig, SPK_pub,
 *                              SPK_sig, SPK_id, opt(OPK_pub, OPK_id) }
 *   2. Verifies SPK_sig over SPK_pub raw SPKI bytes using IK_sign_pub.
 *   3. Verifies IK_dh_sig over IK_dh_pub raw SPKI bytes using IK_sign_pub.
 *   4. Generates an ephemeral ECDH P-256 keypair EK_a.
 *   5. Computes:
 *
 *        DH1 = ECDH(IK_dh_priv_a, SPK_pub_b)    // authenticates Alice to Bob
 *        DH2 = ECDH(EK_priv_a,    IK_dh_pub_b)  // authenticates Bob to Alice
 *        DH3 = ECDH(EK_priv_a,    SPK_pub_b)    // ephemeral / forward secrecy
 *        DH4 = ECDH(EK_priv_a,    OPK_pub_b)    // optional, breaks linkability
 *
 *      master = HKDF-SHA-256(
 *                 IKM   = DH1 || DH2 || DH3 [|| DH4],
 *                 salt  = 0x00...0x00 (32 bytes),
 *                 info  = "IRChat-SecretChat-v1-X3DH",
 *                 L     = 64
 *               )
 *
 *      The HKDF output is split as:
 *           rootKey  = master[0..32)        (currently unused; reserved
 *                                            for the optional DH-ratchet
 *                                            extension; bound into the
 *                                            chain key derivation below)
 *           chainKey = master[32..64)
 *
 *      We then mix the rootKey into the initial chainKey by HMAC:
 *           chainKey0 = HMAC-SHA-256(rootKey, chainKey || "init")
 *
 *      so a future v2 that adds a DH-ratchet can extend cleanly without
 *      changing this version's wire format.
 *
 *   6. Sends the first envelope as `type: "init"` with:
 *        ek    : Alice's ephemeral public key (raw SPKI, base64url)
 *        ik    : Alice's identity DH public key (raw SPKI, base64url)
 *        is    : Alice's identity signing public key (raw SPKI, base64url)
 *        spkId : id of Bob's signed prekey used
 *        opkId : id of Bob's one-time prekey used (or null)
 *        n     : 0 (always)
 *        iv    : 12-byte AES-GCM IV
 *        ct    : AES-GCM ciphertext
 *
 * Bob, upon receiving `init`, performs the mirror computations:
 *
 *        DH1 = ECDH(SPK_priv_b,    IK_dh_pub_a)
 *        DH2 = ECDH(IK_dh_priv_b,  EK_pub_a)
 *        DH3 = ECDH(SPK_priv_b,    EK_pub_a)
 *        DH4 = ECDH(OPK_priv_b,    EK_pub_a)   // if opkId present
 *
 * which yields the identical master, chainKey0, and rootKey.
 *
 * # Symmetric ratchet (forward secrecy)
 *
 * Each direction of the chat has its own chain key. We start with chainKey0
 * for *both* directions (initiator and responder share the same starting
 * key, but the AAD includes a per-side direction byte so the same chain key
 * cannot decrypt the wrong direction).
 *
 * Sending one message:
 *     mk        = HMAC-SHA-256(chainKey, [0x01])         (message key)
 *     chainKey' = HMAC-SHA-256(chainKey, [0x02])         (advance)
 *     enc_key   = HKDF(mk, salt=0,  info="msg-enc", 32)
 *     iv        = HKDF(mk, salt=0,  info="msg-iv",  12)
 *     ad        = chatId || sender_dir || n_be4
 *     ct        = AES-GCM-256(enc_key, iv, plaintext, ad)
 *
 * Receiving with chain index `n`:
 *     - If n < expected, look up `mkSkipped[n]`. If found, decrypt with it
 *       (and remove from the skip pool); else reject as replay/duplicate.
 *     - If n > expected, derive and stash skipped `mk`s up to a bounded
 *       limit (MAX_SKIP), advancing the chain.
 *     - Decrypt the message with the resulting `mk`.
 *
 * # Forward secrecy
 *
 * Compromise of the current chain key reveals no past message keys, because
 * advancement is one-way HMAC. Compromise of long-term identity keys does
 * NOT decrypt past sessions because each session's master secret depends on
 * fresh ephemeral and one-time material.
 *
 * # What this protocol does NOT provide (v1)
 *
 *   - Post-compromise security / "future secrecy" — there is no DH-ratchet
 *     within an established session. If both endpoints' chain keys leak,
 *     all *future* messages in the session are decryptable until the chat
 *     is destroyed and re-established. This is documented in
 *     `docs/SECRET_CHATS.md` and `SECURITY.md`.
 *   - Multi-device — exactly one identity per browser origin per user.
 *   - Group secret chats.
 *
 * # AAD shape
 *
 * AAD = chatId(utf8) || 0x7C || dir(1) || n_be4
 * dir = 0x49 ('I' = initiator) | 0x52 ('R' = responder)
 *
 * Binding `chatId` and direction into the AAD prevents the server (or a
 * MITM with envelope-level access) from re-targeting an envelope to a
 * different chat or replaying it as the opposite direction.
 */

import {
  aesGcmDecrypt,
  aesGcmEncrypt,
  base64UrlToBytes,
  bytesToBase64Url,
  concatBytes,
  ecdhDeriveBits,
  ecdsaVerify,
  exportPublicSpki,
  generateEcdhKey,
  generateRatchetKeyPair,
  hkdf,
  hmacSha256,
  importEcdhPublicSpki,
  importEcdsaPublicSpki,
  kdfRK,
  utf8ToBytes,
  type Bytes,
} from './crypto';

export const PROTOCOL_VERSION = 1;
export const PROTOCOL_VERSION_V2 = 2;
export const MAX_SKIP = 100;
export const HKDF_INFO_X3DH = utf8ToBytes('IRChat-SecretChat-v1-X3DH');
export const HKDF_INFO_MSG_ENC = utf8ToBytes('IRChat-SecretChat-v1-msg-enc');
export const HKDF_INFO_MSG_IV = utf8ToBytes('IRChat-SecretChat-v1-msg-iv');
const ZERO_SALT_32 = new Uint8Array(32);

export type Direction = 'I' | 'R';

export interface PeerBundlePublic {
  identityKey: string; // base64url SPKI of IK_sign (ECDSA)
  identityDhKey: string; // base64url SPKI of IK_dh (ECDH)
  identityDhSig: string; // base64url ECDSA signature over IK_dh raw SPKI
  signedPrekey: string; // base64url SPKI of SPK (ECDH)
  signedPrekeySig: string; // base64url ECDSA signature over SPK raw SPKI
  signedPrekeyId: number;
  oneTimePrekey: { keyId: number; publicKey: string } | null;
}

export interface OwnIdentity {
  signing: CryptoKeyPair; // ECDSA P-256
  identityDh: CryptoKeyPair; // ECDH P-256
}

export interface OwnSignedPrekey {
  keyId: number;
  pair: CryptoKeyPair;
}

export interface OwnOneTimePrekey {
  keyId: number;
  pair: CryptoKeyPair;
}

export interface SessionState {
  /** Our role in the chat. Bound into AAD. */
  direction: Direction;
  /** Outbound (sending) chain key. */
  ckSend: Bytes;
  /** Outbound counter. Incremented on every successful encrypt. */
  ns: number;
  /** Inbound (receiving) chain key. */
  ckRecv: Bytes;
  /** Inbound counter. Incremented on every successful decrypt. */
  nr: number;
  /** Skipped message keys, keyed by inbound chain index. */
  mkSkipped: Map<number, Bytes>;
}

export interface InitialEnvelopeMeta {
  ek: string; // base64url SPKI
  ik: string; // base64url SPKI of initiator IK_dh
  is: string; // base64url SPKI of initiator IK_sign (so receiver can record it)
  spkId: number;
  opkId: number | null;
}

export interface DecodedEnvelope {
  v: number;
  type: 'init' | 'msg';
  n: number;
  iv: string;
  ct: string;
  // Only present for type === 'init':
  init?: InitialEnvelopeMeta;
}

// ── X3DH (initiator) ────────────────────────────────────────────────────────

export async function verifyPeerBundle(bundle: PeerBundlePublic): Promise<{
  ikSignPub: CryptoKey;
  ikDhPub: CryptoKey;
  spkPub: CryptoKey;
  opkPub: CryptoKey | null;
}> {
  const ikSignPub = await importEcdsaPublicSpki(base64UrlToBytes(bundle.identityKey));
  const ikDhPubBytes = base64UrlToBytes(bundle.identityDhKey);
  const ikDhSig = base64UrlToBytes(bundle.identityDhSig);
  const spkPubBytes = base64UrlToBytes(bundle.signedPrekey);
  const spkSig = base64UrlToBytes(bundle.signedPrekeySig);

  const ikDhOk = await ecdsaVerify(ikSignPub, ikDhSig, ikDhPubBytes);
  if (!ikDhOk) throw new Error('Peer bundle: IK_dh signature invalid');
  const spkOk = await ecdsaVerify(ikSignPub, spkSig, spkPubBytes);
  if (!spkOk) throw new Error('Peer bundle: signed prekey signature invalid');

  const ikDhPub = await importEcdhPublicSpki(ikDhPubBytes);
  const spkPub = await importEcdhPublicSpki(spkPubBytes);
  const opkPub = bundle.oneTimePrekey
    ? await importEcdhPublicSpki(base64UrlToBytes(bundle.oneTimePrekey.publicKey))
    : null;
  return { ikSignPub, ikDhPub, spkPub, opkPub };
}

async function deriveMasterFromDhs(dhs: Bytes[]): Promise<{
  rootKey: Bytes;
  chainKey: Bytes;
}> {
  const ikm = concatBytes(...dhs);
  const out = await hkdf(ikm, ZERO_SALT_32, HKDF_INFO_X3DH, 64);
  const rootKey = out.slice(0, 32) as Bytes;
  const chainKey0 = out.slice(32, 64) as Bytes;
  // Mix rootKey into chainKey so a future v2 that introduces an explicit
  // DH-ratchet doesn't have to renumber the wire format.
  const chainKey = await hmacSha256(rootKey, concatBytes(chainKey0, utf8ToBytes('init')));
  return { rootKey, chainKey };
}

/**
 * Initiator-side X3DH-lite.
 *
 * Returns:
 *   - the freshly-derived `chainKey`,
 *   - the ephemeral keypair (so the caller can ship its public part in the
 *     init envelope),
 *   - rootKey (currently unused at runtime; persisted for forward-compat).
 */
export async function initiatorDeriveSession(params: {
  ownIdentityDh: CryptoKeyPair;
  peerBundle: PeerBundlePublic;
}): Promise<{
  chainKey: Bytes;
  rootKey: Bytes;
  ephemeral: CryptoKeyPair;
  initMeta: Omit<InitialEnvelopeMeta, 'is' | 'ik'>;
}> {
  const { ikDhPub, spkPub, opkPub } = await verifyPeerBundle(params.peerBundle);
  const ek = await generateEcdhKey();

  const dh1 = await ecdhDeriveBits(params.ownIdentityDh.privateKey, spkPub);
  const dh2 = await ecdhDeriveBits(ek.privateKey, ikDhPub);
  const dh3 = await ecdhDeriveBits(ek.privateKey, spkPub);
  const dhs = [dh1, dh2, dh3];
  if (opkPub) {
    const dh4 = await ecdhDeriveBits(ek.privateKey, opkPub);
    dhs.push(dh4);
  }

  const { rootKey, chainKey } = await deriveMasterFromDhs(dhs);
  const ekSpki = await exportPublicSpki(ek.publicKey);
  return {
    chainKey,
    rootKey,
    ephemeral: ek,
    initMeta: {
      ek: bytesToBase64Url(ekSpki),
      spkId: params.peerBundle.signedPrekeyId,
      opkId: params.peerBundle.oneTimePrekey?.keyId ?? null,
    },
  };
}

/**
 * Responder-side X3DH-lite. Derives the matching session keys from the
 * `init` envelope material the initiator sent.
 */
export async function responderDeriveSession(params: {
  ownIdentityDh: CryptoKeyPair;
  ownSignedPrekey: CryptoKeyPair;
  ownOneTimePrekey: CryptoKeyPair | null;
  initMeta: InitialEnvelopeMeta;
}): Promise<{ chainKey: Bytes; rootKey: Bytes }> {
  const ekPub = await importEcdhPublicSpki(base64UrlToBytes(params.initMeta.ek));
  const ikAPub = await importEcdhPublicSpki(base64UrlToBytes(params.initMeta.ik));

  const dh1 = await ecdhDeriveBits(params.ownSignedPrekey.privateKey, ikAPub);
  const dh2 = await ecdhDeriveBits(params.ownIdentityDh.privateKey, ekPub);
  const dh3 = await ecdhDeriveBits(params.ownSignedPrekey.privateKey, ekPub);
  const dhs = [dh1, dh2, dh3];
  if (params.ownOneTimePrekey) {
    const dh4 = await ecdhDeriveBits(params.ownOneTimePrekey.privateKey, ekPub);
    dhs.push(dh4);
  }

  return deriveMasterFromDhs(dhs);
}

// ── Symmetric ratchet ───────────────────────────────────────────────────────

const TAG_MK: Bytes = new Uint8Array([0x01]);
const TAG_CK: Bytes = new Uint8Array([0x02]);

async function ratchetStep(ck: Bytes): Promise<{ mk: Bytes; nextCk: Bytes }> {
  const mk = await hmacSha256(ck, TAG_MK);
  const nextCk = await hmacSha256(ck, TAG_CK);
  return { mk, nextCk };
}

async function deriveMessageKey(mk: Bytes): Promise<{ encKey: Bytes; iv: Bytes }> {
  const encKey = await hkdf(mk, ZERO_SALT_32, HKDF_INFO_MSG_ENC, 32);
  const iv = await hkdf(mk, ZERO_SALT_32, HKDF_INFO_MSG_IV, 12);
  return { encKey, iv };
}

function buildAad(chatId: string, dirSent: Direction, n: number): Bytes {
  const idBytes = utf8ToBytes(chatId);
  const sep = new Uint8Array([0x7c]);
  const dir = new Uint8Array([dirSent.charCodeAt(0)]);
  const nbe = new Uint8Array(4);
  new DataView(nbe.buffer).setUint32(0, n, false);
  return concatBytes(idBytes, sep, dir, nbe);
}

export async function encryptMessage(
  state: SessionState,
  chatId: string,
  plaintext: Bytes,
): Promise<{ n: number; iv: Bytes; ciphertext: Bytes; newState: SessionState }> {
  const { mk, nextCk } = await ratchetStep(state.ckSend);
  const { encKey, iv } = await deriveMessageKey(mk);
  const n = state.ns;
  const ad = buildAad(chatId, state.direction, n);
  const ct = await aesGcmEncrypt(encKey, iv, plaintext, ad);
  const newState: SessionState = {
    ...state,
    ckSend: nextCk,
    ns: state.ns + 1,
    mkSkipped: new Map(state.mkSkipped),
  };
  return { n, iv, ciphertext: ct, newState };
}

export async function decryptMessage(
  state: SessionState,
  chatId: string,
  n: number,
  ciphertext: Bytes,
): Promise<{ plaintext: Bytes; newState: SessionState }> {
  const peerDirection: Direction = state.direction === 'I' ? 'R' : 'I';
  const ad = buildAad(chatId, peerDirection, n);

  const newSkipped = new Map(state.mkSkipped);

  // Out-of-order: key from skip pool.
  if (n < state.nr) {
    const mk = newSkipped.get(n);
    if (!mk) {
      throw new Error(`Replay or duplicate message at index ${n}`);
    }
    newSkipped.delete(n);
    const { encKey, iv } = await deriveMessageKey(mk);
    const pt = await aesGcmDecrypt(encKey, iv, ciphertext, ad);
    return {
      plaintext: pt,
      newState: { ...state, mkSkipped: newSkipped },
    };
  }

  // In-order or future: advance the receiving chain, stashing keys we skip.
  let ck = state.ckRecv;
  let i = state.nr;
  if (n - i > MAX_SKIP) {
    throw new Error(`Too many skipped messages: ${n - i}`);
  }
  while (i < n) {
    const step = await ratchetStep(ck);
    newSkipped.set(i, step.mk);
    ck = step.nextCk;
    i += 1;
  }
  const { mk, nextCk } = await ratchetStep(ck);
  const { encKey, iv } = await deriveMessageKey(mk);
  const plaintext = await aesGcmDecrypt(encKey, iv, ciphertext, ad);
  return {
    plaintext,
    newState: {
      ...state,
      ckRecv: nextCk,
      nr: n + 1,
      mkSkipped: newSkipped,
    },
  };
}

// ── Envelope (de)serialization ──────────────────────────────────────────────

export function encodeEnvelope(
  type: 'init' | 'msg',
  n: number,
  iv: Bytes,
  ciphertext: Bytes,
  init?: InitialEnvelopeMeta,
): string {
  const obj: DecodedEnvelope = {
    v: PROTOCOL_VERSION,
    type,
    n,
    iv: bytesToBase64Url(iv),
    ct: bytesToBase64Url(ciphertext),
  };
  if (init) obj.init = init;
  return bytesToBase64Url(utf8ToBytes(JSON.stringify(obj)));
}

export function decodeEnvelope(s: string): DecodedEnvelope | DecodedEnvelopeV2 {
  const json = new TextDecoder().decode(base64UrlToBytes(s));
  const obj = JSON.parse(json);
  if (typeof obj !== 'object' || obj === null) throw new Error('Bad envelope');
  if (obj.v === PROTOCOL_VERSION_V2) return decodeEnvelopeV2(obj);
  if (obj.v !== PROTOCOL_VERSION) throw new Error(`Unsupported envelope version: ${obj.v}`);
  if (obj.type !== 'init' && obj.type !== 'msg') throw new Error('Bad envelope type');
  if (typeof obj.n !== 'number' || obj.n < 0) throw new Error('Bad envelope n');
  if (typeof obj.iv !== 'string' || typeof obj.ct !== 'string') throw new Error('Bad envelope payload');
  if (obj.type === 'init' && (!obj.init || typeof obj.init !== 'object')) {
    throw new Error('Init envelope missing handshake fields');
  }
  return obj as DecodedEnvelope;
}

// ════════════════════════════════════════════════════════════════════════════
// Phase 17 — Double Ratchet (v2)
//
// Adds a DH ratchet step on top of the existing symmetric ratchet. Every time
// the message direction changes, a fresh ECDH keypair is generated, a new
// rootKey + chain key is derived via KDF_RK, and the sending/receiving chain
// keys reset. This gives POST-COMPROMISE SECURITY: after a key compromise,
// the very next DH ratchet step re-establishes secrecy.
// ════════════════════════════════════════════════════════════════════════════

/** v2 session state. Includes DH ratchet keypairs + root key. */
export interface SessionStateV2 {
  version: 2;
  direction: Direction;
  rootKey: Bytes;
  /** Our current DH ratchet keypair. Rotated on every DH ratchet step. */
  DHs: CryptoKeyPair;
  /** Their current DH ratchet public key. Updated when we see a new `dh` in envelope. */
  DHr: CryptoKey | null;
  /** Send chain key. null until first DH ratchet completes for this direction. */
  CKs: Bytes | null;
  /** Recv chain key. null until we see first message from peer. */
  CKr: Bytes | null;
  ns: number;
  nr: number;
  /** Length of the previous sending chain (for skip accounting). */
  pn: number;
  /**
   * Skipped message keys. Key format: `${base64url(DHr_pub_spki)}:${n}`.
   * This allows skipping across multiple DH ratchet steps (unlike v1 which
   * only skipped within a single chain).
   */
  mkSkipped: Map<string, Bytes>;
}

/** v2 envelope adds `dh` (sender's ratchet public) and `pn`. */
export interface DecodedEnvelopeV2 {
  v: 2;
  type: 'init' | 'msg';
  dh: string;   // sender's current DH ratchet public (base64url SPKI)
  pn: number;   // previous chain message count
  n: number;
  iv: string;   // base64url 12 bytes
  ct: string;   // base64url ciphertext
  init?: InitialEnvelopeMeta;
}

function decodeEnvelopeV2(obj: Record<string, unknown>): DecodedEnvelopeV2 {
  if (obj.type !== 'init' && obj.type !== 'msg') throw new Error('Bad v2 envelope type');
  if (typeof obj.dh !== 'string') throw new Error('Bad v2 envelope: missing dh');
  if (typeof obj.pn !== 'number' || obj.pn < 0) throw new Error('Bad v2 envelope: bad pn');
  if (typeof obj.n !== 'number' || obj.n < 0) throw new Error('Bad v2 envelope: bad n');
  if (typeof obj.iv !== 'string' || typeof obj.ct !== 'string') throw new Error('Bad v2 envelope payload');
  if (obj.type === 'init' && (!obj.init || typeof obj.init !== 'object')) {
    throw new Error('v2 init envelope missing handshake fields');
  }
  return obj as unknown as DecodedEnvelopeV2;
}

export function encodeEnvelopeV2(
  type: 'init' | 'msg',
  dhPubSpki: Bytes,
  pn: number,
  n: number,
  iv: Bytes,
  ciphertext: Bytes,
  init?: InitialEnvelopeMeta,
): string {
  const obj: DecodedEnvelopeV2 = {
    v: PROTOCOL_VERSION_V2,
    type,
    dh: bytesToBase64Url(dhPubSpki),
    pn,
    n,
    iv: bytesToBase64Url(iv),
    ct: bytesToBase64Url(ciphertext),
  };
  if (init) obj.init = init;
  return bytesToBase64Url(utf8ToBytes(JSON.stringify(obj)));
}

/** Helper: get base64url-encoded SPKI of a CryptoKey (for skip-map keys). */
async function dhPubId(key: CryptoKey): Promise<string> {
  return bytesToBase64Url(await exportPublicSpki(key));
}

/** Build skip-map key from DH public and chain index. */
function skipKey(dhPubB64: string, n: number): string {
  return `${dhPubB64}:${n}`;
}

// ── DH ratchet step ────────────────────────────────────────────────────────

/**
 * Perform a DH ratchet step: peer sent a new DH public key.
 *
 * 1. Store skipped keys for remaining messages in current recv chain.
 * 2. Derive new recv chain from rootKey + DH(our_old, their_new).
 * 3. Generate fresh DH keypair.
 * 4. Derive new send chain from rootKey + DH(our_new, their_new).
 */
async function dhRatchetStep(
  state: SessionStateV2,
  newDHrPub: CryptoKey,
): Promise<SessionStateV2> {
  const newSkipped = new Map(state.mkSkipped);

  // Store skipped keys from the current receiving chain (if CKr exists).
  if (state.CKr !== null && state.DHr !== null) {
    const oldDhrId = await dhPubId(state.DHr);
    let ck = state.CKr;
    for (let i = state.nr; i < state.nr + MAX_SKIP; i++) {
      // We don't know how many messages were in the old chain, so we only
      // skip up to what we actually need when decrypting individual messages.
      // The actual skip happens in decryptMessageV2 before calling this.
      break; // placeholder — real skip done per-message
    }
    void ck; void oldDhrId; // satisfy linter; real skip done in decrypt path
  }

  // Step 2: derive new recv chain
  const dhOutput1 = await ecdhDeriveBits(state.DHs.privateKey, newDHrPub);
  const { newRK: rk1, chainKey: newCKr } = await kdfRK(state.rootKey, dhOutput1);

  // Step 3: new DH keypair
  const newDHs = await generateRatchetKeyPair();

  // Step 4: derive new send chain
  const dhOutput2 = await ecdhDeriveBits(newDHs.privateKey, newDHrPub);
  const { newRK: rk2, chainKey: newCKs } = await kdfRK(rk1, dhOutput2);

  return {
    ...state,
    rootKey: rk2,
    DHs: newDHs,
    DHr: newDHrPub,
    CKs: newCKs,
    CKr: newCKr,
    pn: state.ns,
    ns: 0,
    nr: 0,
    mkSkipped: newSkipped,
  };
}

// ── v2 encrypt ──────────────────────────────────────────────────────────────

export async function encryptMessageV2(
  state: SessionStateV2,
  chatId: string,
  plaintext: Bytes,
): Promise<{ envelope: DecodedEnvelopeV2; newState: SessionStateV2 }> {
  // Phase 17.2 (2026-04-30) — auto DH-ratchet step on send when CKs=null.
  // Это стандартный Signal Double Ratchet: после responder bootstrap CKs=null
  // (responder ждёт первое сообщение чтобы установить чат). Когда responder
  // ОТВЕЧАЕТ — он должен сделать первый own DH-step:
  //   1. Сгенерить новую DH keypair
  //   2. dhOut = ECDH(newDHs.priv, peer.DHr)
  //   3. kdfRK(rootKey, dhOut) → newRK + newCKs
  //   4. Обновить session: DHs=newDHs, rootKey=newRK, CKs=newCKs, pn=ns, ns=0
  // Также применимо к initiator если peer уже сделал DH-step (мы рестартим
  // нашу send-chain каждый раз когда получаем новый peer.DHr — это
  // happens in decrypt path via dhRatchetStep).
  let workingState = state;
  if (!state.CKs) {
    if (!state.DHr) {
      throw new Error('v2: cannot send — CKs=null and no peer DH yet (peer must send first)');
    }
    const newDHs = await generateRatchetKeyPair();
    const dhOut = await ecdhDeriveBits(newDHs.privateKey, state.DHr);
    const { newRK, chainKey: newCKs } = await kdfRK(state.rootKey, dhOut);
    workingState = {
      ...state,
      DHs: newDHs,
      rootKey: newRK,
      CKs: newCKs,
      pn: state.ns,
      ns: 0,
      mkSkipped: new Map(state.mkSkipped),
    };
  }

  const { mk, nextCk } = await ratchetStep(workingState.CKs!);
  const { encKey, iv } = await deriveMessageKey(mk);
  const n = workingState.ns;
  const ad = buildAad(chatId, workingState.direction, n);
  const ct = await aesGcmEncrypt(encKey, iv, plaintext, ad);

  const dhPubSpki = await exportPublicSpki(workingState.DHs.publicKey);

  const newState: SessionStateV2 = {
    ...workingState,
    CKs: nextCk,
    ns: workingState.ns + 1,
    mkSkipped: new Map(workingState.mkSkipped),
  };

  const envelope: DecodedEnvelopeV2 = {
    v: PROTOCOL_VERSION_V2,
    type: n === 0 && !state.DHr ? 'init' : 'msg',
    dh: bytesToBase64Url(dhPubSpki),
    pn: state.pn,
    n,
    iv: bytesToBase64Url(iv),
    ct: bytesToBase64Url(ct),
  };

  return { envelope, newState };
}

// ── v2 decrypt ──────────────────────────────────────────────────────────────

export async function decryptMessageV2(
  state: SessionStateV2,
  chatId: string,
  env: DecodedEnvelopeV2,
): Promise<{ plaintext: Bytes; newState: SessionStateV2 }> {
  const peerDirection: Direction = state.direction === 'I' ? 'R' : 'I';
  const peerDHPub = await importEcdhPublicSpki(base64UrlToBytes(env.dh));
  const peerDHId = env.dh; // base64url string for skip-map keying

  let currentState = { ...state, mkSkipped: new Map(state.mkSkipped) };

  // 1. Check skip pool first (out-of-order from any past chain).
  const skippedKey = skipKey(peerDHId, env.n);
  const cachedMk = currentState.mkSkipped.get(skippedKey);
  if (cachedMk) {
    currentState.mkSkipped.delete(skippedKey);
    const { encKey, iv } = await deriveMessageKey(cachedMk);
    const ad = buildAad(chatId, peerDirection, env.n);
    const pt = await aesGcmDecrypt(encKey, iv, base64UrlToBytes(env.ct), ad);
    return { plaintext: pt, newState: currentState };
  }

  // 2. If peer's DH key changed → DH ratchet step.
  const currentDHrId = currentState.DHr ? await dhPubId(currentState.DHr) : null;
  if (peerDHId !== currentDHrId) {
    // Skip remaining messages in OLD recv chain (pn = how many they sent).
    if (currentState.CKr !== null && currentState.DHr !== null) {
      const oldDhrId = await dhPubId(currentState.DHr);
      let ck = currentState.CKr;
      const skip = env.pn - currentState.nr;
      if (skip > MAX_SKIP) throw new Error(`Too many skipped in old chain: ${skip}`);
      for (let i = currentState.nr; i < env.pn; i++) {
        const { mk: skMk, nextCk } = await ratchetStep(ck);
        currentState.mkSkipped.set(skipKey(oldDhrId, i), skMk);
        ck = nextCk;
      }
    }
    // Perform DH ratchet
    currentState = await dhRatchetStep(currentState, peerDHPub);
  }

  // 3. Symmetric ratchet within current recv chain.
  if (!currentState.CKr) throw new Error('v2: CKr not initialized');
  let ck = currentState.CKr;
  const skip = env.n - currentState.nr;
  if (skip > MAX_SKIP) throw new Error(`Too many skipped messages: ${skip}`);
  if (skip < 0) throw new Error(`Replay or duplicate at index ${env.n}`);

  // Skip intermediate keys in this chain.
  for (let i = currentState.nr; i < env.n; i++) {
    const { mk: skMk, nextCk } = await ratchetStep(ck);
    currentState.mkSkipped.set(skipKey(peerDHId, i), skMk);
    ck = nextCk;
  }

  // Decrypt target message.
  const { mk, nextCk } = await ratchetStep(ck);
  const { encKey, iv } = await deriveMessageKey(mk);
  const ad = buildAad(chatId, peerDirection, env.n);
  const pt = await aesGcmDecrypt(encKey, iv, base64UrlToBytes(env.ct), ad);

  currentState.CKr = nextCk;
  currentState.nr = env.n + 1;

  return { plaintext: pt, newState: currentState };
}

// ── Safety number ───────────────────────────────────────────────────────────

/**
 * Compute the human-comparable "safety number" for a pair of identity public
 * keys (ECDSA SPKI bytes). Both endpoints must compute the same number; we
 * sort the inputs lexicographically to make the function symmetric.
 *
 * Output: 60 decimal digits, formatted as 12 groups of 5.
 */
export async function computeSafetyNumber(
  ourIkSignPubSpki: Bytes,
  theirIkSignPubSpki: Bytes,
): Promise<string> {
  const a = bytesToBase64Url(ourIkSignPubSpki);
  const b = bytesToBase64Url(theirIkSignPubSpki);
  const [first, second] = a < b ? [ourIkSignPubSpki, theirIkSignPubSpki] : [theirIkSignPubSpki, ourIkSignPubSpki];
  const concatenated = concatBytes(first, second);
  // Iterated SHA-256 (5200 rounds) — slightly stretches and matches Signal's
  // approach in spirit. Pure WebCrypto: just chain `digest` calls.
  let h: Bytes = concatenated;
  const subtle = crypto.subtle;
  for (let i = 0; i < 5200; i += 1) {
    h = new Uint8Array(await subtle.digest('SHA-256', h));
  }
  // Take the first 30 bytes, group into 6-byte windows of 5 decimal digits.
  const groups: string[] = [];
  for (let i = 0; i < 12; i += 1) {
    const slice = h.slice(i * 2, i * 2 + 4);
    const view = new DataView(slice.buffer, slice.byteOffset, slice.byteLength);
    const n = view.getUint32(0, false) % 100000;
    groups.push(n.toString().padStart(5, '0'));
  }
  return groups.join(' ');
}
